"use client";

import * as React from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown, Check, ArrowRight, MessageSquare, Terminal, Sparkles, Search, Shield } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { CommandKTrigger } from "@/components/ui/CommandKSearch";
import { ThemeToggle } from "@/components/ui/ThemeToggle";
import { useScrollLock } from "@/hooks/useScrollLock";

// Category structure for services in the Mega Menu
const megaMenuData = {
  services: [
    {
      title: "Build",
      description: "Launch new products and modern customer touchpoints.",
      items: [
        { name: "Custom Software", href: "/custom-software-development" },
        { name: "Web Applications", href: "/web-application-development" },
        { name: "Offshore Team (US/UK)", href: "/offshore-software-development-india" },
        { name: "Dubai & GCC Hub", href: "/software-development-company-dubai" },
        { name: "Bangalore Hub", href: "/software-development-company-bangalore" },
        { name: "Mumbai Hub", href: "/software-development-company-mumbai" },
        { name: "Gurgaon Hub", href: "/software-development-company-gurgaon" },
      ],
    },
    {
      title: "Automate",
      description: "Streamline workflows and save manual hours.",
      items: [
        { name: "Custom CRM Systems", href: "/crm-development" },
        { name: "ERP Systems Development", href: "/erp-development" },
        { name: "B2B Dealer Portals", href: "/b2b-portal-development" },
        { name: "GST Inventory Software", href: "/custom-inventory-software-gst" },
        { name: "AI Automation Workflows", href: "/ai-automation" },
      ],
    },
    {
      title: "Scale",
      description: "Scale operations reliably without downtime.",
      items: [
        { name: "Cloud Infrastructure", href: "/cloud-devops-infrastructure" },
        { name: "DevOps Pipelines", href: "/cloud-devops-infrastructure" },
        { name: "API Development", href: "/services?tab=scale" },
        { name: "Security Audits", href: "/services?tab=scale" },
      ],
    },
    {
      title: "Grow",
      description: "Drive traffic and increase conversion rates.",
      items: [
        { name: "SEO Optimization", href: "/seo" },
        { name: "Performance Audits", href: "/services?tab=grow" },
        { name: "Technical Consulting", href: "/services?tab=grow" },
      ],
    },
  ],
  solutions: [
    { name: "Cost Calculator (ROI)", href: "/tools/software-cost-calculator", desc: "Compare custom build vs 3-year recurring SaaS subscription costs." },
    { name: "Warehouse Management (WMS)", href: "/warehouse-management-software", desc: "Enterprise warehouse space slotting, 3PL logistics, and barcode inventory control." },
    { name: "B2B Supply Chain & Hyperpure", href: "/b2b-supply-chain-software", desc: "HoReCa food procurement, automated vendor portals, and cold-chain distribution." },
    { name: "Zoho CRM Alternative", href: "/alternatives/zoho-crm-alternative", desc: "Zero per-seat licensing CRM with native WhatsApp automation." },
    { name: "Salesforce Alternative", href: "/alternatives/salesforce-alternative", desc: "Lightweight enterprise CRM without consulting bloat." },
    { name: "Custom SaaS (TalentFlow)", href: "/solutions/saas", desc: "AI-powered talent sourcing & vetting platform." },
  ],
  industries: [
    { name: "Restaurant & Cloud Kitchen POS", href: "/restaurant-management-software" },
    { name: "Warehouse & 3PL Logistics", href: "/warehouse-management-software" },
    { name: "B2B Food & Restaurant Supply", href: "/b2b-supply-chain-software" },
    { name: "Insurance Broker & POSP CRM", href: "/insurance-crm-software" },
    { name: "Legal Case & Advocate Suite", href: "/legal-case-management-software" },
    { name: "Staffing & Recruitment ATS", href: "/staffing-agency-software" },
    { name: "Healthcare & Clinic HMIS", href: "/healthcare-software" },
    { name: "Pharmacy Billing & Wholesale", href: "/pharmacy-software" },
    { name: "Logistics & Fleet TMS", href: "/logistics-software" },
    { name: "Gym & Fitness Studio CRM", href: "/gym-management-software" },
    { name: "Real Estate & Builders", href: "/real-estate-crm" },
    { name: "Telecalling & Call Centers", href: "/telecalling-crm" },
  ],
};

export const Navbar: React.FC = () => {
  const pathname = usePathname();
  if (pathname === "/pitch" || pathname === "/brand/logo-preview") return null;

  const [isOpen, setIsOpen] = React.useState(false);
  const [activeDropdown, setActiveDropdown] = React.useState<string | null>(null);
  const [mobileExpanded, setMobileExpanded] = React.useState<string | null>(null);
  const [isScrolled, setIsScrolled] = React.useState(false);

  // Prevent background scrolling when mobile navigation drawer is open
  useScrollLock(isOpen);

  React.useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: "About", href: "/about" },
    { name: "Services", href: "/services", mega: "services" },
    { name: "Solutions", href: "/solutions", mega: "solutions" },
    { name: "Industries", href: "/industries", mega: "industries" },
    { name: "Cost Calculator", href: "/tools/software-cost-calculator" },
    { name: "Demos", href: "/demos" },
    { name: "Pricing", href: "/pricing" },
    { name: "Case Studies", href: "/case-studies" },
    { name: "Resources", href: "/resources" },
  ];

  // Dedicated single header: hide marketing navbar on CRM routes
  if (pathname?.startsWith("/crm")) {
    return null;
  }

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 w-full transition-all duration-300 border-b print:hidden ${
          isScrolled
            ? "border-[#111318]/10 shadow-sm bg-white/95 backdrop-blur-md text-[#111318]"
            : "border-[#111318]/5 bg-[#F8F8F6]/95 backdrop-blur-md text-[#111318]"
        }`}
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 sm:h-18 items-center justify-between">
            {/* Branding Logo */}
            <div className="flex items-center mr-3 lg:mr-6 shrink-0">
              <Link href="/" className="flex items-center gap-2.5 sm:gap-3 group">
                <div className="relative h-8 w-8 sm:h-9 sm:w-9 shrink-0 rounded-xl overflow-hidden flex items-center justify-center group-hover:scale-105 transition-transform duration-300">
                  {/* Official Software Company 3D Monolith Brand Mark */}
                  <img src="/brand/app-icon.svg?v=3" alt="Johnson Softwares" className="w-full h-full object-contain" />
                </div>
                <span className="flex flex-col leading-none select-none">
                  <span className="font-heading font-black text-xs sm:text-sm lg:text-base tracking-[0.14em] uppercase text-[#111318] leading-tight">
                    JOHNSON
                  </span>
                  <span className="text-[#2563EB] tracking-[0.22em] font-heading font-extrabold text-[9px] sm:text-[10px] lg:text-xs uppercase group-hover:text-blue-700 transition-colors duration-300 leading-tight">
                    SOFTWARES
                  </span>
                </span>
              </Link>
            </div>

            {/* Desktop Navigation Link Cluster - visible on lg (1024px+) with compact, non-overflowing spacing */}
            <nav className="hidden lg:flex items-center gap-2.5 xl:gap-4 2xl:gap-6 shrink-0">
              {navLinks.map((link) => {
                const isActive = pathname === link.href;
                const hasMega = !!link.mega;

                return (
                  <div
                    key={link.name}
                    className="relative"
                    onMouseEnter={() => hasMega && setActiveDropdown(link.mega)}
                    onMouseLeave={() => hasMega && setActiveDropdown(null)}
                  >
                    {hasMega ? (
                      <button
                        className={`flex items-center gap-0.5 xl:gap-1 py-2 text-xs xl:text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                          activeDropdown === link.mega ? "text-[#2563EB]" : "text-[#5E636B] hover:text-[#111318]"
                        }`}
                      >
                        {link.name}
                        <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-300 ${activeDropdown === link.mega ? "rotate-180" : ""}`} />
                      </button>
                    ) : (
                      <Link
                        href={link.href}
                        className={`py-2 text-xs xl:text-sm font-medium transition-colors whitespace-nowrap ${
                          isActive ? "text-[#2563EB] font-semibold" : "text-[#5E636B] hover:text-[#111318]"
                        }`}
                      >
                        {link.name}
                      </Link>
                    )}

                    {/* Mega Menu Dropdowns */}
                    {hasMega && activeDropdown === link.mega && (
                      <AnimatePresence>
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 10 }}
                          transition={{ duration: 0.2 }}
                          className="absolute left-1/2 -translate-x-1/2 top-full w-screen max-w-4xl bg-white shadow-xl border border-[#111318]/10 rounded-2xl overflow-hidden z-50 text-[#111318]"
                        >
                          {link.mega === "services" && (
                            <div className="grid grid-cols-4 gap-6 p-8">
                              {megaMenuData.services.map((cat) => (
                                <div key={cat.title} className="flex flex-col gap-3">
                                  <h4 className="font-heading font-semibold text-xs text-[#2563EB] uppercase tracking-widest border-b border-[#111318]/10 pb-2">
                                    {cat.title}
                                  </h4>
                                  <p className="text-xs text-[#5E636B] leading-relaxed mb-1">{cat.description}</p>
                                  <ul className="flex flex-col gap-2">
                                    {cat.items.map((item) => (
                                      <li key={item.name}>
                                        <Link
                                          href={item.href}
                                          className="text-sm font-medium text-[#111318] hover:text-[#2563EB] flex items-center gap-1 group/item transition-colors"
                                        >
                                          {item.name}
                                          <ArrowRight className="h-3.5 w-3.5 opacity-0 -translate-x-1 group-hover/item:opacity-100 group-hover/item:translate-x-0 transition-all duration-200 text-[#2563EB]" />
                                        </Link>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              ))}
                            </div>
                          )}

                          {link.mega === "solutions" && (
                            <div className="grid grid-cols-3 gap-6 p-6">
                              {megaMenuData.solutions.map((sol) => (
                                <Link
                                  key={sol.name}
                                  href={sol.href}
                                  className="group/sol p-4 rounded-xl hover:bg-[#F8F8F6] transition-colors border border-transparent hover:border-[#111318]/10"
                                >
                                  <h4 className="font-heading font-semibold text-sm text-[#111318] group-hover/sol:text-[#2563EB] transition-colors flex items-center gap-1.5">
                                    {sol.name}
                                    <ArrowRight className="h-3.5 w-3.5 opacity-0 group-hover/sol:opacity-100 group-hover/sol:translate-x-0.5 transition-all duration-200" />
                                  </h4>
                                  <p className="text-xs text-[#5E636B] mt-1 leading-relaxed">{sol.desc}</p>
                                </Link>
                              ))}
                            </div>
                          )}

                          {link.mega === "industries" && (
                            <div className="p-6">
                              <h4 className="font-heading font-semibold text-xs text-[#2563EB] uppercase tracking-widest border-b border-[#111318]/10 pb-2 mb-4">
                                Target Verticals
                              </h4>
                              <div className="grid grid-cols-2 gap-4">
                                {megaMenuData.industries.map((ind) => (
                                  <Link
                                    key={ind.name}
                                    href={ind.href}
                                    className="text-sm text-[#111318] hover:text-[#2563EB] font-medium flex items-center gap-2 group/ind transition-colors"
                                  >
                                    <Check className="h-4 w-4 text-[#2563EB] opacity-50 group-hover/ind:opacity-100 transition-opacity" />
                                    {ind.name}
                                  </Link>
                                ))}
                              </div>
                            </div>
                          )}
                        </motion.div>
                      </AnimatePresence>
                    )}
                  </div>
                );
              })}
            </nav>

            {/* Nav Actions (Search, Consultation, Menu Toggle) */}
            <div className="flex items-center gap-2 sm:gap-2.5 shrink-0">
              {/* Command K Search Trigger */}
              <CommandKTrigger />

              {/* Direct CRM Portal Access - shown on xl (1280px+) so 1024px tablet screens never get crowded */}
              <Link
                href="/crm"
                className="hidden xl:inline-flex whitespace-nowrap shrink-0 px-3 py-1.5 rounded-lg text-xs font-heading font-bold bg-white text-[#111318] border border-[#111318]/15 hover:border-[#2563EB] hover:text-[#2563EB] transition-all duration-200 items-center gap-1.5 shadow-sm"
                title="Launch Johnson Softwares CRM Portal"
              >
                <Shield className="w-3.5 h-3.5 text-[#2563EB]" />
                <span>CRM Portal</span>
              </Link>

              {/* 7-Day Free Trial Link - visible on all standard laptops & desktops (xl+) */}
              <div className="hidden xl:block shrink-0" suppressHydrationWarning>
                <Link
                  href="/register"
                  suppressHydrationWarning
                  className="whitespace-nowrap shrink-0 px-3 py-1.5 rounded-lg text-xs font-heading font-bold text-cyan-600 dark:text-cyan-400 bg-cyan-500/10 border border-cyan-500/25 hover:bg-cyan-500/20 transition-all flex items-center gap-1.5 shadow-xs"
                >
                  <Sparkles className="w-3.5 h-3.5 text-cyan-600 dark:text-cyan-400" />
                  <span>7-Day Free Trial</span>
                </Link>
              </div>

              {/* Consultation Booking Action */}
              <div className="hidden sm:block shrink-0">
                <Link
                  href="/contact"
                  className="inline-flex items-center justify-center px-3 py-1.5 xl:px-4 xl:py-2 rounded-xl bg-[#2563EB] hover:bg-blue-700 text-white text-xs font-semibold shadow-sm transition-all whitespace-nowrap"
                >
                  Get Free Consultation
                </Link>
              </div>

              {/* Mobile & Tablet Menu Action (visible on screens below lg: 1024px) */}
              <button
                onClick={() => setIsOpen(!isOpen)}
                aria-label="Toggle Navigation Menu"
                className="lg:hidden p-2 rounded-lg text-[#111318] hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer"
              >
                {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Fixed Header Spacer to prevent top page content from being obscured */}
      <div className="h-16 sm:h-18 w-full shrink-0 pointer-events-none" aria-hidden="true" />

      {/* Mobile & Tablet Drawer (Responsive Overlay) */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            className="lg:hidden fixed top-16 sm:top-18 left-0 right-0 bottom-0 z-40 bg-white dark:bg-[#0B0F19] border-b border-[#111318]/10 shadow-2xl overflow-y-auto overscroll-contain"
          >
            <div className="px-5 pt-4 pb-28 space-y-4 max-w-lg mx-auto">
              {/* Search bar inside drawer */}
              <div onClick={() => setIsOpen(false)}>
                <CommandKTrigger className="w-full justify-between py-2.5 px-3.5" />
              </div>

              {/* Quick Action Shortcuts */}
              <div className="grid grid-cols-3 gap-2 pb-3 border-b border-[#111318]/10">
                <Link
                  href="/demos"
                  onClick={() => setIsOpen(false)}
                  className="flex flex-col items-center justify-center p-2.5 rounded-xl bg-slate-100 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 text-center active:scale-95 transition-all"
                >
                  <Terminal className="w-4 h-4 text-[#2563EB] mb-1" />
                  <span className="text-[11px] font-heading font-bold text-foreground leading-tight">Live Demos</span>
                  <span className="text-[9px] text-text-muted">6 Showrooms</span>
                </Link>

                <Link
                  href="/resources/project-estimator"
                  onClick={() => setIsOpen(false)}
                  className="flex flex-col items-center justify-center p-2.5 rounded-xl bg-slate-100 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 text-center active:scale-95 transition-all"
                >
                  <Sparkles className="w-4 h-4 text-amber-500 mb-1" />
                  <span className="text-[11px] font-heading font-bold text-foreground leading-tight">Estimator</span>
                  <span className="text-[9px] text-text-muted">Instant Cost</span>
                </Link>

                <a
                  href="https://wa.me/919880893416?text=Hi%20Johnson%20Softwares,%20I'm%20inquiring%20about%20your%20software%20engineering%20services."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-col items-center justify-center p-2.5 rounded-xl bg-emerald-500/10 dark:bg-emerald-900/20 border border-emerald-500/30 text-center active:scale-95 transition-all"
                >
                  <MessageSquare className="w-4 h-4 text-emerald-600 mb-1" />
                  <span className="text-[11px] font-heading font-bold text-emerald-600 dark:text-emerald-400 leading-tight">WhatsApp</span>
                  <span className="text-[9px] text-emerald-600/80 dark:text-emerald-400/80">Chat Now</span>
                </a>
              </div>

              {/* Navigation Links with interactive accordions */}
              <div className="space-y-1">
                {navLinks.map((link) => {
                  const hasMega = !!link.mega;
                  const isExpanded = mobileExpanded === link.mega;

                  return (
                    <div key={link.name} className="border-b border-[#111318]/5 pb-1">
                      <div className="flex items-center justify-between">
                        <Link
                          href={link.href}
                          onClick={() => setIsOpen(false)}
                          className="flex-1 flex items-center min-h-[44px] text-base font-semibold text-foreground hover:text-[#2563EB] py-1"
                        >
                          {link.name}
                        </Link>
                        {hasMega && (
                          <button
                            type="button"
                            onClick={() => setMobileExpanded(isExpanded ? null : link.mega!)}
                            aria-label={`Toggle ${link.name} submenu`}
                            className="p-2.5 text-text-muted hover:text-[#2563EB] rounded-lg cursor-pointer"
                          >
                            <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? "rotate-180 text-[#2563EB]" : ""}`} />
                          </button>
                        )}
                      </div>

                      {/* Mega Items Accordion */}
                      {hasMega && isExpanded && (
                        <div className="pl-3 pb-3 pt-1 space-y-3">
                          {link.mega === "services" && (
                            <div className="grid grid-cols-2 gap-3">
                              {megaMenuData.services.map((cat) => (
                                <div key={cat.title} className="flex flex-col gap-1.5">
                                  <span className="text-[10px] font-mono font-bold text-[#2563EB] uppercase tracking-wider">{cat.title}</span>
                                  {cat.items.map((item) => (
                                    <Link
                                      key={item.name}
                                      href={item.href}
                                      onClick={() => setIsOpen(false)}
                                      className="text-xs text-foreground/80 hover:text-[#2563EB] py-1"
                                    >
                                      {item.name}
                                    </Link>
                                  ))}
                                </div>
                              ))}
                            </div>
                          )}

                          {link.mega === "solutions" && (
                            <div className="flex flex-col gap-2">
                              {megaMenuData.solutions.map((sol) => (
                                <Link
                                  key={sol.name}
                                  href={sol.href}
                                  onClick={() => setIsOpen(false)}
                                  className="text-xs text-foreground/80 hover:text-[#2563EB] py-1.5 flex items-center justify-between"
                                >
                                  <span className="font-medium">{sol.name}</span>
                                  <ArrowRight className="w-3 h-3 text-text-muted" />
                                </Link>
                              ))}
                            </div>
                          )}

                          {link.mega === "industries" && (
                            <div className="grid grid-cols-2 gap-2">
                              {megaMenuData.industries.map((ind) => (
                                <Link
                                  key={ind.name}
                                  href={ind.href}
                                  onClick={() => setIsOpen(false)}
                                  className="text-xs text-foreground/80 hover:text-[#2563EB] py-1.5"
                                >
                                  {ind.name}
                                </Link>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Bottom Actions */}
              <div className="pt-4 space-y-2.5">
                <Link
                  href="/crm"
                  onClick={() => setIsOpen(false)}
                  className="w-full py-3 rounded-xl text-center text-xs font-bold bg-[#111318] text-white flex items-center justify-center gap-2 shadow-sm"
                >
                  <Shield className="w-4 h-4 text-[#2563EB]" />
                  <span>Open CRM Portal (Login)</span>
                </Link>
                <Link
                  href="/register"
                  onClick={() => setIsOpen(false)}
                  className="w-full py-3 rounded-xl text-center text-xs font-bold bg-[#2563EB]/10 text-[#2563EB] border border-[#2563EB]/25 flex items-center justify-center gap-2"
                >
                  <Sparkles className="w-4 h-4 text-[#2563EB]" />
                  <span>Start 7-Day Free CRM Trial</span>
                </Link>
                <Link
                  href="/contact"
                  onClick={() => setIsOpen(false)}
                  className="w-full py-3 rounded-xl text-center text-xs font-bold bg-[#2563EB] hover:bg-blue-700 text-white flex items-center justify-center gap-2 shadow-md"
                >
                  Get Free Consultation
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
